import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    TextField,
    Typography as TypographyMUI
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { format, parseISO } from "date-fns";
import { addContactHistory, getAllContactHistory } from "../../../../api/apiCall";
import { useAuth } from "../../../../auth/auth";
import AppModal from "../../../../components/AppModal/AppModal";

const Timeline = ({ items, style }: { items: { dot?: React.ReactNode; children?: React.ReactNode }[]; style?: React.CSSProperties }) => (
    <Box style={style}>
        {items.map((item, i) => (
            <Box key={i} sx={{ display: "flex", gap: 1.5, pb: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    {item.dot}
                    {i < items.length - 1 && (
                        <Box sx={{ flex: 1, width: "2px", bgcolor: "#e8e8e8", mt: 0.5 }} />
                    )}
                </Box>
                <Box sx={{ flex: 1 }}>{item.children}</Box>
            </Box>
        ))}
    </Box>
);

interface ContactHistoryProps {
  profileInfo: any;
  getWorkerInfo: any;
}

const ContactHistory: React.FC<ContactHistoryProps> = ({ profileInfo, getWorkerInfo }) => {
    const {state} = useAuth();
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState([]);

    const handleSubmit = async () => {
        setLoading(true);
        const response = await addContactHistory(state.user?.token, {
            workerId: profileInfo?.id,
            callerUserId: state.user?.id,
            notes: notes
        });

        if(response?.status === 201) {
            setLoading(false);
            getWorkerInfo();
            setOpen(false);
        }
    };

    const getAllContacts = async () => {
        const response = await getAllContactHistory(state.user?.token, profileInfo?.id);
        if(response?.status === 200) {
            setContacts(response.data);
        }
    };

    useEffect(() => {
        getAllContacts();
    }, [])

    return (
        <>
            <div>
                <TypographyMUI variant="body1" mt={1}>
                    <strong>Contact History</strong>
                    <IconButton
                        size="small"
                        sx={{ border: "none", marginBottom: 0.3 }}
                        aria-label="Add"
                        onClick={() => setOpen(true)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </IconButton>
                </TypographyMUI>

                <Timeline
                    style={{ marginTop: 16 }}
                    items={contacts.map((item: any) => ({
                        dot: (
                            <div
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: "#1976d2",
                                }}
                            />
                        ),
                        children: (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                }}
                            >
                                <span style={{ color: "#333", fontSize: 14 }}>
                                    {format(parseISO(item?.callDate), "dd MMM yyyy")}
                                </span>
                                <span style={{ fontSize: 16, color: "#1976d2", fontWeight: 600 }}>
                                    {item.callerUser}
                                </span>
                                <span style={{ color: "#656565" }}>
                                    {item.notes}
                                </span>
                            </div>
                        ),
                    }))}
                />
            </div>
            <AppModal
                title={`Add Contact History for ${profileInfo?.name} ${profileInfo?.surname}`}
                onCancel={() => setOpen(false)}
                open={open}
                footer={[
                    <div className="center-button-div">
                        <Button
                            disabled={loading}
                            variant="contained"
                            sx={{ boxShadow: 'none', mb: 2, width: 300 }}
                            onClick={handleSubmit}>
                            {loading? <CircularProgress size={20} style={{color: 'white'}} /> : 'Add Contact History'}
                        </Button>
                    </div>
                ]}>
                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr" }} gap={2}>
                        <TypographyMUI variant="body1">
                            <strong>Date:</strong> {format(new Date(), "dd/MM/yyyy")}
                        </TypographyMUI>
                        <TextField
                            label="Notes"
                            placeholder="Write notes here..."
                            multiline
                            rows={4}
                            size="small"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                    </Box>
            </AppModal>
        </>
    );
};

export default ContactHistory;
